import { useCallback, useState } from "react";
import { useSearchParams } from "react-router";

function usePageParams() {
  const [searchParams] = useSearchParams();
  // Helper to update URL without React Router
  const updateURL = useCallback((key: string, value: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.replaceState({}, "", url.toString());
  }, []);

  // Helper for simple URL string parameters
  const useURLParam = <T extends string>(
    key: string,
    defaultValue?: T
  ): [T | undefined, (value: T) => void] => {
    const [state, setState] = useState<T | undefined>(() => {
      const urlValue = searchParams.get(key) as T;
      return urlValue || defaultValue;
    });

    const setter = useCallback(
      (value: T) => {
        setState(value);
        updateURL(key, value);
      },
      [key]
    );

    return [state, setter];
  };

  // Helper for JSON URL parameters
  const useJSONParam = <T>(
    key: string,
    defaultValue: T
  ): [T, (value: T) => void] => {
    const [state, setState] = useState<T>(() => {
      const param = searchParams.get(key);
      if (param) {
        try {
          return JSON.parse(param);
        } catch {
          return defaultValue;
        }
      }
      return defaultValue;
    });

    const setter = useCallback(
      (value: T) => {
        setState(value);
        updateURL(key, JSON.stringify(value));
      },
      [key]
    );

    return [state, setter];
  };

  return { useURLParam, useJSONParam, updateURL };
}

export { usePageParams };
